/**
 * Fixed-timestep game loop using requestAnimationFrame.
 * Targets 30fps for that authentic pixel art feel.
 */

export interface GameLoopCallbacks {
  /** Called at fixed timestep intervals. dt is in seconds. */
  update: (dt: number) => void;
  /** Called once per animation frame after updates. */
  render: () => void;
}

export class GameLoop {
  private rafId = 0;
  private lastTime = 0;
  private running = false;
  private accumulator = 0;

  /** Fixed timestep in seconds (30fps). */
  private readonly fixedDt: number;

  constructor(
    private callbacks: GameLoopCallbacks,
    targetFps = 30,
  ) {
    this.fixedDt = 1 / targetFps;
  }

  start(): void {
    if (this.running) return;
    this.running = true;
    this.lastTime = performance.now();
    this.accumulator = 0;
    this.rafId = requestAnimationFrame((t) => this.tick(t));
  }

  stop(): void {
    this.running = false;
    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
      this.rafId = 0;
    }
  }

  private tick(timestamp: number): void {
    if (!this.running) return;

    const dt = Math.min((timestamp - this.lastTime) / 1000, 0.1); // Cap to avoid spiral of death
    this.lastTime = timestamp;
    this.accumulator += dt;

    // Fixed timestep updates
    while (this.accumulator >= this.fixedDt) {
      this.callbacks.update(this.fixedDt);
      this.accumulator -= this.fixedDt;
    }

    // Render at display rate
    this.callbacks.render();

    this.rafId = requestAnimationFrame((t) => this.tick(t));
  }
}
